import { AttributeValidationHandler } from '../ui-element.type';
import { hasValue } from '../../global/helpers';
import { ObjectWithStringKeys } from '../../global/utils.type';
import { BrandColors } from '../../global/helpers-marketing';
import type { UIBadge } from './ui-badge';
import type { UIBadgeContentTypes, UIBadgeLayouts } from './ui-badge.type';

/**
 * @type {UIBadgeLayouts}
 */
export const BadgeLayouts: UIBadgeLayouts = {
    Default: 'default' /* Sales badge */,
    Pin: 'pin',
    Segment: 'segment',
    SegmentSmall: 'segment-small',
} as const;

/**
 * @type {UIBadgeContentTypes}
 */
export const BadgeContentTypes: UIBadgeContentTypes = {
    Tag: 'tag',
    Icon: 'icon',
    Count: 'count',
} as const;

/**
 * @param {UIBadge} component
 * @param {string} value
 * @private
 */
export const validateBadgeColor: AttributeValidationHandler<UIBadge> = (
    component: UIBadge,
    value: string
): void => {
    let allowedOptionsLabel: string;
    let deprecatedOptions: Array<string>;

    switch (component.layout) {
        case BadgeLayouts.Pin: {
            allowedOptionsLabel =
                'Allowed options are: "keyword", "discovery", "neutral", "error", "warning", "information", "success".';
            deprecatedOptions = [
                'blue',
                'pineapple',
                'bark',
                'orange',
                'yellow',
                'turquoise',
                'lilac',
            ];
            break;
        }
        case BadgeLayouts.Segment:
        case BadgeLayouts.SegmentSmall: {
            allowedOptionsLabel =
                'Allowed options are: "orange", "yellow", "lilac", "turquoise"';
            deprecatedOptions = ['blue', 'pineapple', 'bark'];
            break;
        }
        /* Sales badge */
        case BadgeLayouts.Default:
        default: {
            allowedOptionsLabel =
                'Allowed options are: "dark-1", "dark-2", "light-1", "light-2", "light-3".';
            deprecatedOptions = [
                'blue',
                'pineapple',
                'bark',
                'orange',
                'yellow',
                'turquoise',
                'lilac',
            ];
            break;
        }
    }

    if (deprecatedOptions.includes(value)) {
        console.warn(
            `${value} - color is deprecated. ${allowedOptionsLabel}`,
            component
        );
        return;
    }

    if (!hasValue({ ...BrandColors, Yellow: 'yellow' }, value)) {
        console.warn(
            `${value} - value does not exist. ${allowedOptionsLabel}`,
            component
        );
    }
};

/**
 * @param {UIBadge} component
 * @param {string} value
 * @private
 */
export const validateBadgeLayout: AttributeValidationHandler<UIBadge> = (
    component: UIBadge,
    value: string
): void => {
    if (!hasValue(BadgeLayouts as unknown as ObjectWithStringKeys, value)) {
        console.warn(
            `${value} - value does not exist. Allowed options are: "pin", "segment" and "segment-small".`,
            component
        );
    }
};

/**
 * @param {UIBadge} component
 * @param {string} value
 * @private
 */
export const validateBadgeContentType: AttributeValidationHandler<UIBadge> = (
    component: UIBadge,
    value: string
): void => {
    if (
        !hasValue(BadgeContentTypes as unknown as ObjectWithStringKeys, value)
    ) {
        console.warn(
            `${value} - value does not exist. Allowed options are: "tag", "count" and "icon".`,
            component
        );
    }
};
